/**
 * Shops-feature data layer + i18n namespace registration (design revamp).
 *
 * Owns the `shops` i18n namespace and the TanStack Query hooks for the
 * "Find shops" screen. Following the feature-owns-its-namespace pattern
 * (see `features/account/api.ts`, `features/chat/chat-store.ts`), the
 * bundles are registered here via `i18n.addResourceBundle` rather than in
 * the shared `@/lib/i18n` (which is outside this feature's allowed-touch
 * list). RU/KY/EN are all first-class (ADR-028); RU is the documented
 * fallback for any missing KY/EN key (REQ-PRD-§13.2-A).
 *
 * Two queries back the screen:
 *   - `useSellers(params)`            GET /marketplace/sellers (the seller
 *                                     finder; typed by `listSellers` in
 *                                     `@/lib/garden-api`). Drives the list +
 *                                     map. `q` is debounced at the screen.
 *   - `useSellerInventory(sellerId)`  GET /marketplace/search?seller_id=… —
 *                                     a small sample of the seller's products
 *                                     for the detail sheet, reusing the
 *                                     marketplace browse envelope.
 *
 * No PII travels here: seller business data, ratings, languages, operating
 * hours, and public catalogue products only (seller phone numbers are not in
 * these payloads — the call/WhatsApp CTAs in the detail sheet are demo no-ops
 * in this slice).
 */
import {
  useQuery,
  type UseQueryResult,
} from "@tanstack/react-query";

import { apiClient } from "@/lib/api-client";
import i18n, { initI18n } from "@/lib/i18n";
import {
  listSellers,
  type SellerFinderParams,
  type SellerSummary,
  type SellerSummaryListResponse,
} from "@/lib/garden-api";
import type { ProductSearchItem } from "@/features/marketplace/useMarketplaceBrowse";

import shopsRu from "@/locales/ru/shops.json";
import shopsKy from "@/locales/ky/shops.json";
import shopsEn from "@/locales/en/shops.json";

export const SHOPS_NS = "shops";

/**
 * Register the `shops` namespace on the shared i18n singleton (idempotent).
 * `initI18n()` is idempotent and guarantees the instance is initialised
 * before `addResourceBundle` (the bundle methods only exist post-init).
 * Called at the top of the screen's render so the keys resolve regardless of
 * import order (tests re-init i18n in `beforeEach`).
 */
export function registerShopsI18n(): void {
  initI18n();
  if (!i18n.hasResourceBundle("ru", SHOPS_NS)) {
    i18n.addResourceBundle("ru", SHOPS_NS, shopsRu, true, true);
  }
  if (!i18n.hasResourceBundle("ky", SHOPS_NS)) {
    i18n.addResourceBundle("ky", SHOPS_NS, shopsKy, true, true);
  }
  if (!i18n.hasResourceBundle("en", SHOPS_NS)) {
    i18n.addResourceBundle("en", SHOPS_NS, shopsEn, true, true);
  }
}

// Best-effort eager registration for the production path (initI18n() runs at
// boot in main.tsx). In tests where init happens in beforeEach, the screen
// calls registerShopsI18n() on render.
registerShopsI18n();

export type { SellerSummary, SellerFinderParams };

/** staleTime for the finder list — the seller catalogue changes slowly. */
export const SELLERS_STALE_TIME_MS = 60_000;
/** staleTime for a seller's sample inventory. */
export const INVENTORY_STALE_TIME_MS = 60_000;
/** Sample-inventory page size shown in the detail sheet (3–4 items). */
export const INVENTORY_SAMPLE_LIMIT = 4;

/**
 * Stable query key for the seller finder. The param snapshot is normalised
 * (undefined → null) so an absent filter and an explicitly-cleared filter
 * share one cache entry.
 */
export function sellersQueryKey(
  params: SellerFinderParams,
): readonly unknown[] {
  return [
    "shops",
    "sellers",
    {
      category: params.category ?? null,
      district: params.district ?? null,
      q: params.q ?? null,
    },
  ];
}

/** GET /marketplace/sellers — the finder list + map source. */
export function useSellers(
  params: SellerFinderParams,
): UseQueryResult<SellerSummaryListResponse, Error> {
  return useQuery<SellerSummaryListResponse, Error>({
    queryKey: sellersQueryKey(params),
    queryFn: (): Promise<SellerSummaryListResponse> => listSellers(params),
    staleTime: SELLERS_STALE_TIME_MS,
  });
}

/** Query key for a single seller's sample inventory. */
export function sellerInventoryQueryKey(
  sellerId: string | null,
): readonly unknown[] {
  return ["shops", "inventory", sellerId];
}

/**
 * Build the marketplace browse path scoped to one seller. Reuses the public
 * search endpoint with `mode=browse` (the empty-q list-all path) plus the
 * `seller_id` filter, capped to a small sample for the detail sheet.
 */
export function buildSellerInventoryPath(sellerId: string): string {
  const params = new URLSearchParams();
  params.set("mode", "browse");
  params.set("seller_id", sellerId);
  params.set("limit", String(INVENTORY_SAMPLE_LIMIT));
  return `/marketplace/search?${params.toString()}`;
}

/** Envelope of the seller-scoped browse call (the items are all we render). */
export interface SellerInventoryResponse {
  items: ProductSearchItem[];
}

/**
 * GET /marketplace/search?seller_id=… — a sample of the seller's products
 * for the detail sheet. `enabled` is false until a seller is selected, so no
 * request fires while the sheet is closed.
 */
export function useSellerInventory(
  sellerId: string | null,
): UseQueryResult<SellerInventoryResponse, Error> {
  return useQuery<SellerInventoryResponse, Error>({
    queryKey: sellerInventoryQueryKey(sellerId),
    queryFn: (): Promise<SellerInventoryResponse> =>
      apiClient.get<SellerInventoryResponse>(
        buildSellerInventoryPath(sellerId as string),
      ),
    enabled: sellerId !== null,
    staleTime: INVENTORY_STALE_TIME_MS,
  });
}
